import { faTimes, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Button } from '@tryhackme/thm-ui-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isFreeAndNotPausedUser } from 'src/common/helpers/subscription';
import { useExpiredVmTime } from 'src/common/hooks';
import { useGetUserQuery, useTrackCTAClickMutation, useTrackEventMutation } from 'src/common/slices';
import { anonymousId } from 'src/common/tracking/data';
import * as S from './attack-box-expiring-notice.styles';
interface AttackBoxExpiringNoticeProps {
  expires?: string | Date | undefined;
  onOpenPremiumModal: () => void;
}
export const AttackBoxExpiringNotice = ({
  expires,
  onOpenPremiumModal
}: AttackBoxExpiringNoticeProps) => {
  const [canShowPopup, setCanShowPopup] = useState(false);
  const hasShown30MinWarning = useRef(false);
  const hasShown10MinWarning = useRef(false);
  const handleExpiry = useCallback(() => setCanShowPopup(false), []);
  const {
    formattedTime,
    millisecondsUntilExpiry
  } = useExpiredVmTime(expires, handleExpiry);
  const {
    data: userData
  } = useGetUserQuery();
  const canBuyPremium = isFreeAndNotPausedUser(userData?.data?.user);
  useEffect(() => {
    if (!expires || millisecondsUntilExpiry <= 0 || !canBuyPremium) {
      setCanShowPopup(false);
      return;
    }
    const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
    const twentyMinutes = 20 * 60 * 1000; // 20 minutes in milliseconds
    const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds

    if (millisecondsUntilExpiry <= thirtyMinutes && millisecondsUntilExpiry > twentyMinutes &&
    // Only show between 30-20 minutes
    !hasShown30MinWarning.current) {
      setCanShowPopup(true);
      hasShown30MinWarning.current = true;
      return;
    }
    if (millisecondsUntilExpiry <= tenMinutes && millisecondsUntilExpiry > 0 && !hasShown10MinWarning.current) {
      setCanShowPopup(true);
      hasShown10MinWarning.current = true;
      return;
    }
  }, [millisecondsUntilExpiry, expires, canBuyPremium]);
  const handleClose = useCallback(() => {
    setCanShowPopup(false);
  }, []);
  const handleGoPremium = useCallback(() => {
    setCanShowPopup(false);
    onOpenPremiumModal();
  }, [onOpenPremiumModal]);
  const handleMaybeLater = useCallback(() => {
    setCanShowPopup(false);
  }, []);
  if (!canShowPopup) return null;
  return <AttackBoxExpiringPopup formattedTime={formattedTime} onClose={handleClose} onGoPremium={handleGoPremium} onMaybeLater={handleMaybeLater} data-sentry-element="AttackBoxExpiringPopup" data-sentry-component="AttackBoxExpiringNotice" data-sentry-source-file="attack-box-expiring-notice.tsx" />;
};
interface AttackBoxExpiringPopupProps {
  formattedTime: string;
  onClose: () => void;
  onGoPremium: () => void;
  onMaybeLater: () => void;
}
const AttackBoxExpiringPopup = ({
  formattedTime,
  onClose,
  onGoPremium,
  onMaybeLater
}: AttackBoxExpiringPopupProps) => {
  const isFeatureOn = useFeatureIsOn('promote-premium-before-attackbox-expires-experiment');
  const [trackEvent] = useTrackEventMutation();
  const [trackCTAClick] = useTrackCTAClickMutation();
  const trackButtonClick = useCallback((button_text: string) => trackCTAClick({
    button_text,
    page: 'AttackBox Expiring Notice',
    anonymousId
  }), [trackCTAClick]);
  const handleClose = useCallback(() => {
    trackButtonClick('Close');
    onClose();
  }, [trackButtonClick, onClose]);
  const handleGoPremium = useCallback(() => {
    trackButtonClick('Go Premium');
    onGoPremium();
  }, [trackButtonClick, onGoPremium]);
  const handleMaybeLater = useCallback(() => {
    trackButtonClick('Maybe later');
    onMaybeLater();
  }, [trackButtonClick, onMaybeLater]);

  // Track popup view only when feature is on
  useEffect(() => {
    if (isFeatureOn) {
      trackEvent({
        event: 'view-popup',
        properties: {
          name: 'attack-box-expiring-notice'
        }
      });
    }
  }, [isFeatureOn, trackEvent]);
  if (!isFeatureOn) return null;
  return <S.Popup data-sentry-element="unknown" data-sentry-component="AttackBoxExpiringPopup" data-sentry-source-file="attack-box-expiring-notice.tsx">
      <S.DismissButton onClick={handleClose} aria-label="Close popup" data-sentry-element="unknown" data-sentry-source-file="attack-box-expiring-notice.tsx">
        <FontAwesomeIcon icon={faTimes} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="attack-box-expiring-notice.tsx" />
      </S.DismissButton>

      <S.Header data-sentry-element="unknown" data-sentry-source-file="attack-box-expiring-notice.tsx">
        <S.WarningIcon data-sentry-element="unknown" data-sentry-source-file="attack-box-expiring-notice.tsx">
          <FontAwesomeIcon icon={faTriangleExclamation} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="attack-box-expiring-notice.tsx" />
        </S.WarningIcon>
        <S.Title data-sentry-element="unknown" data-sentry-source-file="attack-box-expiring-notice.tsx">AttackBox expiring soon</S.Title>
      </S.Header>

      <S.Description data-sentry-element="unknown" data-sentry-source-file="attack-box-expiring-notice.tsx">
        Free users can only deploy the AttackBox for 1 hour a day. Go Premium to get unlimited AttackBox time!
      </S.Description>

      <S.Countdown data-sentry-element="unknown" data-sentry-source-file="attack-box-expiring-notice.tsx">
        AttackBox expires in <span>{formattedTime}</span>
      </S.Countdown>

      <S.Buttons data-sentry-element="unknown" data-sentry-source-file="attack-box-expiring-notice.tsx">
        <Button variant="outlined" color="secondary" onClick={handleMaybeLater} data-sentry-element="Button" data-sentry-source-file="attack-box-expiring-notice.tsx">
          Maybe later
        </Button>
        <Button variant="solid" color="primary" onClick={handleGoPremium} data-sentry-element="Button" data-sentry-source-file="attack-box-expiring-notice.tsx">
          Go Premium
        </Button>
      </S.Buttons>
    </S.Popup>;
};